import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import { motion } from 'framer-motion';
import { LegalModal } from '../components/UI/LegalModal';
import { authApi } from '../api/auth';
import { track, EVENTS } from '../lib/mixpanel';
import { getMachhaAsset, AssetImg } from '../lib/machhaAsset';
import { clayStaggerContainer, clayStaggerItem, clayTapScale } from '../constants/animations';
import { AmbientBlobs, type BlobConfig } from '../components/UI/AmbientBlobs';
import styles from './LoginPage.module.css';

const BLOBS: BlobConfig[] = [
  {
    gradient: 'linear-gradient(135deg, rgba(139,92,246,0.25) 0%, rgba(244,114,182,0.18) 100%)',
    position: { top: '-160px', left: '-160px' },
    size: 500,
    animate: { scale: [1, 1.15, 1], x: [0, 20, 0], y: [0, -15, 0], opacity: [0.5, 0.75, 0.5] },
    transition: { repeat: Infinity, duration: 7, ease: 'easeInOut' },
  },
  {
    gradient: 'linear-gradient(135deg, rgba(96,165,250,0.18) 0%, rgba(167,139,250,0.18) 100%)',
    position: { bottom: '-160px', right: '-160px' },
    size: 500,
    animate: { scale: [1, 1.2, 1], x: [0, -25, 0], y: [0, 20, 0], opacity: [0.4, 0.65, 0.4] },
    transition: { repeat: Infinity, duration: 9, ease: 'easeInOut', delay: 1 },
  },
  {
    gradient: 'radial-gradient(ellipse, rgba(139,92,246,0.08) 0%, transparent 70%)',
    position: { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' },
    size: 600,
    animate: { rotate: [0, 15, 0, -15, 0], scale: [1, 1.05, 1] },
    transition: { repeat: Infinity, duration: 12, ease: 'easeInOut' },
  },
];

const features = [
  {
    asset: 'feature-ftp-antenna',
    label: 'FTP Uploads',
    desc: 'Direct from camera',
  },
  {
    asset: 'feature-editor-palette',
    label: 'Photo Editor',
    desc: 'Filters & overlays',
  },
  {
    asset: 'feature-export-configs-box',
    label: 'Export',
    desc: 'Social media ready',
  },
];

export const LoginPage: React.FC = () => {
  usePageTitle('Sign in');
  const [legalModal, setLegalModal] = useState<'terms' | 'privacy' | null>(null);

  return (
    <div className={styles['page']}>
      <AmbientBlobs blobs={BLOBS} />

      <Link
        to="/"
        className={styles['back-link']}
      >
        <svg className={styles['back-icon']} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
        Back to home
      </Link>

      <motion.div
        className={styles['content-wrapper']}
        variants={clayStaggerContainer}
        initial="hidden"
        animate="visible"
      >
        {/* Hero mascot */}
        <motion.div
          className={styles['mascot-container']}
          variants={clayStaggerItem}
          animate={{ y: [0, -10, 0] }}
          transition={{ repeat: Infinity, duration: 3.5, ease: 'easeInOut' }}
        >
          <AssetImg
            src={getMachhaAsset('LandingPageAssets', 'hero-camera-mascot')}
            alt="Pixel Machha mascot"
            className={styles['mascot-img']}
          />
        </motion.div>

        {/* App name */}
        <motion.div
          className={styles['branding']}
          variants={clayStaggerItem}
        >
          <motion.h1 className={styles['app-title']}>
            Pixel Machha
          </motion.h1>
          <motion.p className={styles['app-subtitle']}>
            Professional event photography management
          </motion.p>
        </motion.div>

        {/* Card */}
        <motion.div
          className={styles['card']}
          variants={clayStaggerItem}
        >
          {/* Top gradient accent */}
          <div className={styles['card-accent']} />

          <h2 className={styles['card-title']}>Welcome back</h2>
          <p className={styles['card-description']}>
            Sign in to manage your photography events, FTP uploads, and photo gallery.
          </p>

          <motion.a
            href={authApi.googleLoginUrl()}
            onClick={() => track(EVENTS.LOGIN_STARTED)}
            className={styles['google-button']}
            whileHover={{ scale: 1.02, y: -2 }}
            whileTap={clayTapScale}
            transition={{ type: 'spring', stiffness: 420, damping: 22 }}
          >
            <AssetImg
              src={getMachhaAsset('LandingPageAssets', 'cta-google-logo-clay')}
              alt="Google"
              className={styles['google-logo']}
            />
            Continue with Google
          </motion.a>

          <p className={styles['terms-text']}>
            By signing in, you agree to our{' '}
            <button className={styles['legal-link']} onClick={() => setLegalModal('terms')}>Terms of Service</button>
            {' '}and{' '}
            <button className={styles['legal-link']} onClick={() => setLegalModal('privacy')}>Privacy Policy</button>.
          </p>
        </motion.div>

      {legalModal && (
        <LegalModal type={legalModal} isOpen onClose={() => setLegalModal(null)} />
      )}

        {/* Feature pills with clay assets */}
        <motion.div
          className={styles['features-grid']}
          variants={clayStaggerContainer}
        >
          {features.map((f, i) => (
            <motion.div
              key={f.label}
              className={styles['feature-item']}
              variants={clayStaggerItem}
              whileHover={{ y: -4, scale: 1.05 }}
              transition={{ type: 'spring', stiffness: 400, damping: 20 }}
              custom={i}
            >
              <motion.div
                className={styles['feature-icon-container']}
                animate={{ y: [0, -4, 0] }}
                transition={{ repeat: Infinity, duration: 3, delay: i * 0.5, ease: 'easeInOut' }}
              >
                <AssetImg
                  src={getMachhaAsset('LandingPageAssets', f.asset)}
                  alt={f.label}
                  className={styles['feature-icon']}
                />
              </motion.div>
              <p className={styles['feature-label']}>{f.label}</p>
              <p className={styles['feature-desc']}>{f.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </div>
  );
};
